/**
 * Built-in cloud family catalog (Load Cloud Family).
 *
 * The desktop build has no network access to the real cloud library, so
 * this curated schema set stands in for the cloud: the tool dialog searches
 * it and registers the chosen schema through FamilyLoader, identical to the
 * local Load Family flow. Extend by appending FamilySchema objects.
 */

import { FamilySchema } from '../core/families/FamilySchema';

const door: FamilySchema = {
    version: '1.0.0',
    name: 'Single-Flush Door 910x2110',
    category: 'Doors',
    parameters: [
        { name: 'Width', type: 'Length', defaultValue: 0.91, isInstance: true, group: 'Dimensions' },
        { name: 'Height', type: 'Length', defaultValue: 2.11, isInstance: true, group: 'Dimensions' },
        { name: 'Thickness', type: 'Length', defaultValue: 0.045, isInstance: false, group: 'Dimensions' },
    ],
    constraints: [],
    geometry: [
        {
            id: 'panel',
            operation: 'Extrude',
            name: 'Door Panel',
            profile: [
                { x: 0, y: 0 }, { x: 'Width', y: 0 }, { x: 'Width', y: 'Height' }, { x: 0, y: 'Height' },
            ],
            height: 'Thickness',
            position: { x: '0', y: '0', z: '0' },
        },
    ],
    types: [
        { name: '910 x 2110 mm', values: { Width: 0.91, Height: 2.11 } },
        { name: '820 x 2085 mm', values: { Width: 0.82, Height: 2.085 } },
    ],
};

const windowFamily: FamilySchema = {
    version: '1.0.0',
    name: 'Fixed Window 1200x1200',
    category: 'Windows',
    parameters: [
        { name: 'Width', type: 'Length', defaultValue: 1.2, isInstance: true, group: 'Dimensions' },
        { name: 'Height', type: 'Length', defaultValue: 1.2, isInstance: true, group: 'Dimensions' },
        { name: 'Frame Depth', type: 'Length', defaultValue: 0.05, isInstance: false, group: 'Dimensions' },
    ],
    constraints: [],
    geometry: [
        {
            id: 'glass',
            operation: 'Extrude',
            name: 'Glazing',
            profile: [
                { x: 0, y: 0 }, { x: 'Width', y: 0 }, { x: 'Width', y: 'Height' }, { x: 0, y: 'Height' },
            ],
            height: '0.006',
            position: { x: '0', y: '0', z: '0' },
        },
    ],
    types: [
        { name: '1200 x 1200 mm', values: { Width: 1.2, Height: 1.2 } },
        { name: '1800 x 1200 mm', values: { Width: 1.8, Height: 1.2 } },
    ],
};

const chair: FamilySchema = {
    version: '1.0.0',
    name: 'Side Chair 450x500',
    category: 'Furniture',
    parameters: [
        { name: 'Seat Height', type: 'Length', defaultValue: 0.45, isInstance: false, group: 'Dimensions' },
        { name: 'Width', type: 'Length', defaultValue: 0.45, isInstance: false, group: 'Dimensions' },
        { name: 'Depth', type: 'Length', defaultValue: 0.5, isInstance: false, group: 'Dimensions' },
    ],
    constraints: [],
    geometry: [
        {
            id: 'seat',
            operation: 'Extrude',
            name: 'Seat',
            profile: [
                { x: 0, y: 0 }, { x: 'Width', y: 0 }, { x: 'Width', y: 'Depth' }, { x: 0, y: 'Depth' },
            ],
            height: '0.04',
            position: { x: '0', y: '0', z: 'Seat Height - 0.04' },
        },
    ],
    types: [
        { name: 'Standard', values: { 'Seat Height': 0.45, Width: 0.45, Depth: 0.5 } },
    ],
};

const lightFixture: FamilySchema = {
    version: '1.0.0',
    name: 'Round Downlight 150mm',
    category: 'Lighting Fixtures',
    parameters: [
        { name: 'Diameter', type: 'Length', defaultValue: 0.15, isInstance: false, group: 'Dimensions' },
        { name: 'Wattage', type: 'Integer', defaultValue: 12, isInstance: true, group: 'Electrical' },
    ],
    constraints: [],
    geometry: [
        {
            id: 'housing',
            operation: 'Revolve',
            name: 'Housing',
            profile: [
                { x: 0, y: 0 }, { x: 'Diameter / 2', y: 0 }, { x: 'Diameter / 2', y: 0.06 }, { x: 0, y: 0.06 },
            ],
            position: { x: '0', y: '0', z: '0' },
        },
    ],
    types: [
        { name: '150 mm / 12 W', values: { Diameter: 0.15, Wattage: 12 } },
        { name: '150 mm / 18 W', values: { Diameter: 0.15, Wattage: 18 } },
    ],
};

const handrail: FamilySchema = {
    version: '1.0.0',
    name: 'Pipe Handrail 42mm',
    category: 'Railing',
    parameters: [
        { name: 'Diameter', type: 'Length', defaultValue: 0.042, isInstance: false, group: 'Dimensions' },
        { name: 'Height', type: 'Length', defaultValue: 0.98, isInstance: true, group: 'Constraints' },
    ],
    constraints: [],
    geometry: [
        {
            id: 'rail',
            operation: 'Sweep',
            name: 'Top Rail',
            profile: [
                { x: 0, y: 0 }, { x: 'Diameter', y: 0 }, { x: 'Diameter', y: 'Diameter' }, { x: 0, y: 'Diameter' },
            ],
            path: [{ x: 0, y: 0, z: 'Height' }, { x: 1, y: 0, z: 'Height' }],
            position: { x: '0', y: '0', z: '0' },
        },
    ],
    types: [
        { name: 'Standard 42 mm OD', values: { Diameter: 0.042 } },
    ],
};

export const CLOUD_FAMILY_CATALOG: FamilySchema[] = [
    door, windowFamily, chair, lightFixture, handrail,
];
